/**
 * GA4 product list attribution in the dataLayer.
 *
 * Product data comes from the `data-ga-list-item` attribute rendered by
 * snippets/ga-list-item.liquid.
 *
 * 1. Pushes the standard GA4 `select_item` event when a customer picks a
 *    product from a list: click to the PDP or quick view, quick add, bundle.
 * 2. Remembers every select_item (ours and Addingwell's) per item_id.
 * 3. Adds item_list_id / item_list_name / index to the ecommerce events that
 *    Addingwell pushes, because it does not know the list:
 *    - view_item, add_to_cart: the last select_item of the item (30 min).
 *    - add_to_cart also gets custom item params add_source / add_source_id:
 *      the surface where the add happened (pdp, sticky-atc, bundle, ...).
 *    - cart and checkout events: the list of the first add of the item to
 *      the cart ("first surface wins").
 *
 * The dataLayer.push wrapper changes the items in place before GTM reads
 * them, so every tag that sends ecommerce data from the dataLayer gets them.
 */
(function () {
  const SELECTIONS_KEY = 'movit:ga:selections';
  const CART_LISTS_KEY = 'movit:ga:cartLists';
  const SELECTION_TTL_MS = 30 * 60 * 1000;
  const CART_LIST_TTL_MS = 14 * 24 * 60 * 60 * 1000;
  const PENDING_ADD_TTL_MS = 2 * 60 * 1000;
  const SELECTION_EVENTS = ['view_item'];
  const CART_EVENTS = [
    'remove_from_cart',
    'view_cart',
    'begin_checkout',
    'add_shipping_info',
    'add_payment_info',
    'purchase',
  ];
  const LIST_FIELDS = ['item_list_id', 'item_list_name', 'index'];
  const SOURCE_FIELDS = ['add_source', 'add_source_id'];

  // Adds in progress on this page, waiting for Addingwell's add_to_cart.
  const pendingAdds = {};

  function readStore(storage, key, ttl) {
    try {
      const data = JSON.parse(storage.getItem(key) || '{}');
      const now = Date.now();
      Object.keys(data).forEach((id) => {
        if (!data[id] || now - data[id].ts > ttl) delete data[id];
      });
      return data;
    } catch (error) {
      return {};
    }
  }

  function writeStore(storage, key, data) {
    try {
      storage.setItem(key, JSON.stringify(data));
    } catch (error) {
      // Storage unavailable (private browsing) - attribution is best effort.
    }
  }

  function pick(source, fields) {
    const result = {};
    fields.forEach((field) => {
      if (source && source[field] !== undefined && source[field] !== null && source[field] !== '') {
        result[field] = source[field];
      }
    });
    return result;
  }

  // Sets fields the item does not have yet; never overwrites Addingwell's data.
  function fillMissing(item, values) {
    Object.keys(values).forEach((field) => {
      if (item[field] === undefined || item[field] === null || item[field] === '') item[field] = values[field];
    });
  }

  function rememberSelection(item, ecommerce) {
    if (!item || item.item_id === undefined) return;
    const list = pick(
      {
        item_list_id: item.item_list_id !== undefined ? item.item_list_id : ecommerce.item_list_id,
        item_list_name: item.item_list_name !== undefined ? item.item_list_name : ecommerce.item_list_name,
        index: item.index,
      },
      LIST_FIELDS
    );
    if (!list.item_list_id && !list.item_list_name) return;

    const selections = readStore(window.sessionStorage, SELECTIONS_KEY, SELECTION_TTL_MS);
    selections[String(item.item_id)] = Object.assign(list, { ts: Date.now() });
    writeStore(window.sessionStorage, SELECTIONS_KEY, selections);
  }

  function lastSelection(itemId) {
    return readStore(window.sessionStorage, SELECTIONS_KEY, SELECTION_TTL_MS)[itemId] || null;
  }

  function enrichAddedItem(item) {
    const itemId = String(item.item_id);
    fillMissing(item, pick(lastSelection(itemId), LIST_FIELDS));

    const pending = pendingAdds[itemId];
    if (pending && Date.now() - pending.ts < PENDING_ADD_TTL_MS) {
      fillMissing(item, pick(pending, SOURCE_FIELDS));
    }
    delete pendingAdds[itemId];

    // The first add of the item to the cart sets the list for later cart events.
    const cartLists = readStore(window.localStorage, CART_LISTS_KEY, CART_LIST_TTL_MS);
    if (!cartLists[itemId]) {
      cartLists[itemId] = Object.assign(pick(item, LIST_FIELDS.concat(SOURCE_FIELDS)), { ts: Date.now() });
      writeStore(window.localStorage, CART_LISTS_KEY, cartLists);
    }
  }

  function enrichCartItem(item) {
    const itemId = String(item.item_id);
    const firstAdd = readStore(window.localStorage, CART_LISTS_KEY, CART_LIST_TTL_MS)[itemId];
    fillMissing(item, pick(firstAdd || lastSelection(itemId), LIST_FIELDS.concat(SOURCE_FIELDS)));
  }

  // Drops the first-add list of products that left the cart, so a later add
  // from another place starts fresh.
  function forgetRemovedItems(cartProductIds) {
    const ids = cartProductIds.map(String);
    const cartLists = readStore(window.localStorage, CART_LISTS_KEY, CART_LIST_TTL_MS);
    Object.keys(cartLists).forEach((id) => {
      if (!ids.includes(id)) delete cartLists[id];
    });
    writeStore(window.localStorage, CART_LISTS_KEY, cartLists);
  }

  function syncCartListsWithCart() {
    const cartUrl = (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_url) || '/cart';
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => forgetRemovedItems((cart.items || []).map((line) => line.product_id)))
      .catch(() => {});
  }

  function enrich(message) {
    if (!message || typeof message !== 'object' || !message.event) return;
    const ecommerce = message.ecommerce;
    if (!ecommerce || !Array.isArray(ecommerce.items)) return;

    try {
      if (message.event === 'select_item') {
        ecommerce.items.forEach((item) => rememberSelection(item, ecommerce));
      } else if (message.event === 'add_to_cart') {
        ecommerce.items.forEach(enrichAddedItem);
      } else if (SELECTION_EVENTS.includes(message.event)) {
        ecommerce.items.forEach((item) => fillMissing(item, pick(lastSelection(String(item.item_id)), LIST_FIELDS)));
      } else if (CART_EVENTS.includes(message.event)) {
        ecommerce.items.forEach(enrichCartItem);
        // view_cart lists the whole cart; after a removal the cart is read again.
        if (message.event === 'view_cart') forgetRemovedItems(ecommerce.items.map((item) => item.item_id));
        if (message.event === 'remove_from_cart') setTimeout(syncCartListsWithCart, 1000);
      }
    } catch (error) {
      // Never break the dataLayer because of attribution.
    }
  }

  // GTM replaces dataLayer.push when it loads, so the wrapper is re-applied
  // until the page settles. It stays the outermost function and changes the
  // message before GTM processes it.
  function hookDataLayer() {
    const dataLayer = (window.dataLayer = window.dataLayer || []);
    if (dataLayer.push && dataLayer.push.movitListAttribution) return;

    const originalPush = dataLayer.push;
    const wrappedPush = function () {
      // A waiting select_item must reach GTM before Addingwell's add_to_cart.
      // GTM's own events (gtm.linkClick, gtm.formSubmit, ...) in the same click
      // do not need it, so they leave the push waiting.
      if (deferredPushes.length && Array.prototype.some.call(arguments, (message) => message && message.ecommerce)) {
        flushDeferredPushes();
      }
      for (let i = 0; i < arguments.length; i++) enrich(arguments[i]);
      return originalPush.apply(dataLayer, arguments);
    };
    wrappedPush.movitListAttribution = true;
    dataLayer.push = wrappedPush;
  }

  function pushSelectItem(payload, variantId) {
    const item = Object.assign({}, payload.item, {
      item_list_id: payload.item_list_id,
      item_list_name: payload.item_list_name,
      quantity: 1,
    });
    if (variantId) item.item_variant_id = String(variantId);

    const message = {
      event: 'select_item',
      ecommerce: {
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined,
        item_list_id: payload.item_list_id,
        item_list_name: payload.item_list_name,
        items: [item],
      },
    };
    // Remember the list right away, the push itself waits for the next paint.
    enrich(message);
    pushAfterPaint({ ecommerce: null });
    pushAfterPaint(message);
  }

  // GTM runs the tags of an event synchronously inside dataLayer.push. Pushed
  // from a click handler, they delay the next paint (INP on mobile), so the
  // push waits until the browser has painted the response to the click.
  const deferredPushes = [];
  let flushScheduled = false;

  function flushDeferredPushes() {
    flushScheduled = false;
    deferredPushes.splice(0).forEach((message) => window.dataLayer.push(message));
  }

  function pushAfterPaint(message) {
    deferredPushes.push(message);
    if (flushScheduled) return;
    flushScheduled = true;
    requestAnimationFrame(() => setTimeout(flushDeferredPushes, 0));
    // requestAnimationFrame does not run in a hidden page.
    setTimeout(flushDeferredPushes, 250);
  }

  // Send what is waiting before the page goes away.
  window.addEventListener('pagehide', flushDeferredPushes);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushDeferredPushes();
  });

  function rememberPendingAdd(payload) {
    pendingAdds[String(payload.item.item_id)] = Object.assign(
      pick({ add_source: payload.source, add_source_id: payload.source_id }, SOURCE_FIELDS),
      { ts: Date.now() }
    );
  }

  function readPayload(element) {
    try {
      const payload = JSON.parse(element.getAttribute('data-ga-list-item'));
      return payload && payload.item ? payload : null;
    } catch (error) {
      return null;
    }
  }

  function selectedVariantId(scope) {
    const input = scope && scope.querySelector('[name="id"]');
    return input ? input.value : null;
  }

  function handleAdd(payload, variantId) {
    if (!payload) return;
    hookDataLayer();
    if (payload.select) pushSelectItem(payload, variantId);
    rememberPendingAdd(payload);
  }

  hookDataLayer();
  const hookTimer = setInterval(hookDataLayer, 250);
  window.addEventListener('load', () => {
    hookDataLayer();
    setTimeout(() => clearInterval(hookTimer), 10000);
  });

  // Capture phase: runs before theme handlers stop the event or send the
  // cart request, so select_item always comes before Addingwell's add_to_cart.
  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest) return;

      // Products bundle: one button adds every product in the group.
      const groupButton = event.target.closest('[data-ga-select-group]');
      if (groupButton) {
        const group = groupButton.closest('[data-ga-group]');
        if (!group) return;
        group.querySelectorAll('[data-ga-list-item]').forEach((element) => {
          handleAdd(readPayload(element), selectedVariantId(element));
        });
        return;
      }

      const trigger = event.target.closest('a[data-ga-list-item], button[data-ga-list-item]');
      const payload = trigger && readPayload(trigger);
      if (payload && payload.select) {
        hookDataLayer();
        pushSelectItem(payload);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const element = form && form.querySelector && form.querySelector('[data-ga-list-item]');
      if (element) handleAdd(readPayload(element), selectedVariantId(form));
    },
    true
  );
})();
